import React, { Suspense, useCallback, useEffect, useState } from "react";
import { getTitleHTMLTag } from "../../helpers/tools";
import { TitleType } from "../Types";

import "@life5/protection-calculator/styles.css";

import type { AftermathResult } from "@life5/protection-calculator";

const AftermathFunnel = React.lazy(() =>
  import("@life5/protection-calculator").then((m) => ({
    default: m.AftermathFunnel,
  }))
);
const AftermathCalculator = React.lazy(() =>
  import("@life5/protection-calculator").then((m) => ({
    default: m.AftermathCalculator,
  }))
);
const AftermathSmartFunnel = React.lazy(() =>
  import("@life5/protection-calculator").then((m) => ({
    default: m.AftermathSmartFunnel,
  }))
);

type CalculatorMode = "Funnel" | "Calculator" | "SmartFunnel";

interface ProtectionCalculatorProps {
  data: {
    Mode: CalculatorMode;
    CalculatorTitle?: string;
    HasTitle?: boolean;
    SelectTitleType?: {
      TitleTypeSelection: TitleType;
    };
    RedirectUrl?: string;
  };
}

type CalculatorComponentProps = { onResult?: (result: AftermathResult) => void };

const CALCULATOR_BY_MODE: Record<
  CalculatorMode,
  React.LazyExoticComponent<React.ComponentType<CalculatorComponentProps>>
> = {
  Funnel: AftermathFunnel,
  Calculator: AftermathCalculator,
  SmartFunnel: AftermathSmartFunnel,
};

const CALCULATOR_PLACEHOLDER = <div className="min-h-[400px]" />;

const ProtectionCalculator = ({ data }: ProtectionCalculatorProps) => {
  const Calculator = CALCULATOR_BY_MODE[data.Mode] ?? AftermathSmartFunnel;
  const { RedirectUrl } = data;

  // renderToString cannot wait for a React.lazy chunk: it gives up on the
  // boundary, ships the placeholder and marks it as failed (<!--$!-->), which
  // React then reports at hydration. Mounting the boundary only after the first
  // client render makes both sides emit the very same placeholder, with no
  // Suspense marker to reconcile — the chunk still loads right after mount.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const handleResult = useCallback(
    ({ breakdown }: AftermathResult) => {
      if (!RedirectUrl || typeof window === "undefined") return;
      const url = new URL(RedirectUrl, window.location.origin);
      url.searchParams.set("gap", String(breakdown.gap));
      window.open(url.toString(), "_blank", "noopener,noreferrer");
    },
    [RedirectUrl]
  );

  const title =
    data.HasTitle && data.CalculatorTitle
      ? getTitleHTMLTag(
          data.CalculatorTitle,
          data.SelectTitleType?.TitleTypeSelection || "H2",
          "text-[22px] md:text-[32px] font-[700] tracking-[-0.64px] text-[color:var(--primary-color)] text-center"
        )
      : null;

  return (
    <section className="overflow-hidden">
      <div className="flex flex-col max-w-content mx-auto py-[64px] xl:px-[0px] px-[24px] md:gap-[32px] gap-[24px]">
        {title}
        {mounted ? (
          <Suspense fallback={CALCULATOR_PLACEHOLDER}>
            <Calculator onResult={handleResult} />
          </Suspense>
        ) : (
          CALCULATOR_PLACEHOLDER
        )}
      </div>
    </section>
  );
};

export default ProtectionCalculator;
